import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaInfoCircle,
  FaPaperPlane,
  FaSave,
} from "react-icons/fa";
import api from "../api";

const emptyConfig = {
  smtpHost: "",
  smtpPort: "",
  useTLS: false,
  senderName: "",
  senderEmail: "",
  authUser: "",
  hasAuthPassword: false,
  useSystemConfiguration: false,
};

function InfoHint({ text }) {
  return (
    <span className="group relative inline-flex">
      <FaInfoCircle className="cursor-help text-slate-400 transition group-hover:text-blue-600" />
      <span className="pointer-events-none absolute right-0 top-6 z-30 hidden w-64 rounded-xl bg-slate-950 px-3 py-2 text-xs font-medium leading-relaxed text-white shadow-xl group-hover:block">
        {text}
      </span>
    </span>
  );
}

function ConfigFields({
  value,
  onChange,
  password,
  setPassword,
  clearPassword,
  setClearPassword,
}) {
  return (
    <div className="space-y-4">
      <section className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-2">
        <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
          Host
          <input
            value={value.smtpHost}
            onChange={(event) => onChange("smtpHost", event.target.value)}
            placeholder="smtp.seudominio.com"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400"
          />
        </label>
        <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
          Porta
          <input
            type="number"
            value={value.smtpPort}
            onChange={(event) => onChange("smtpPort", event.target.value)}
            placeholder="587"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400"
          />
        </label>
        <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
          Remetente
          <input
            value={value.senderName}
            onChange={(event) => onChange("senderName", event.target.value)}
            placeholder="Nash"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400"
          />
        </label>
        <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
          E-mail do remetente
          <input
            type="email"
            value={value.senderEmail}
            onChange={(event) => onChange("senderEmail", event.target.value)}
            placeholder="contato@empresa.com"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400"
          />
        </label>
        <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
          Usuário SMTP
          <input
            value={value.authUser}
            onChange={(event) => onChange("authUser", event.target.value)}
            placeholder="contato@empresa.com"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400"
          />
        </label>
        <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
          Senha
          <input
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setClearPassword(false);
            }}
            placeholder={value.hasAuthPassword ? "••••••••" : "Informe a senha"}
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400"
          />
        </label>
      </section>

      <div className="flex flex-wrap items-center gap-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(value.useTLS)}
            onChange={(event) => onChange("useTLS", event.target.checked)}
          />
          TLS/SSL
        </label>
        {value.hasAuthPassword && (
          <label className="inline-flex items-center gap-2 text-rose-600">
            <input
              type="checkbox"
              checked={clearPassword}
              onChange={(event) => {
                setClearPassword(event.target.checked);
                if (event.target.checked) setPassword("");
              }}
            />
            Remover senha
          </label>
        )}
      </div>
    </div>
  );
}

export default function MailSettings() {
  const [activeTab, setActiveTab] = useState("system");
  const [system, setSystem] = useState(emptyConfig);
  const [marketing, setMarketing] = useState(emptyConfig);
  const [systemPassword, setSystemPassword] = useState("");
  const [marketingPassword, setMarketingPassword] = useState("");
  const [clearSystemPassword, setClearSystemPassword] = useState(false);
  const [clearMarketingPassword, setClearMarketingPassword] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  const normalize = (data = {}) => ({
    smtpHost: data.smtpHost || "",
    smtpPort: data.smtpPort === null || data.smtpPort === undefined ? "" : String(data.smtpPort),
    useTLS: Boolean(data.useTLS),
    senderName: data.senderName || "",
    senderEmail: data.senderEmail || "",
    authUser: data.authUser || "",
    hasAuthPassword: Boolean(data.hasAuthPassword),
    useSystemConfiguration: Boolean(data.useSystemConfiguration),
  });

  const load = async () => {
    setBusy("load");
    setError("");
    try {
      const [systemResponse, marketingResponse] = await Promise.all([
        api.get("/mail/configuration"),
        api.get("/mail/marketing/configuration"),
      ]);
      setSystem(normalize(systemResponse.data));
      setMarketing(normalize(marketingResponse.data));
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível carregar as configurações.");
    } finally {
      setBusy("");
    }
  };

  useEffect(() => { load(); }, []);

  const current = activeTab === "system" ? system : marketing;
  const password = activeTab === "system" ? systemPassword : marketingPassword;
  const clearPassword = activeTab === "system" ? clearSystemPassword : clearMarketingPassword;
  const marketingUsesSystem = activeTab === "marketing" && Boolean(marketing.useSystemConfiguration);
  const systemReady = Boolean(
    system.smtpHost &&
    system.smtpPort &&
    system.senderEmail &&
    system.authUser &&
    system.hasAuthPassword
  );

  const updateCurrent = (field, value) => {
    const setter = activeTab === "system" ? setSystem : setMarketing;
    setter((previous) => ({ ...previous, [field]: value }));
  };

  const save = async () => {
    if (marketingUsesSystem && !systemReady) {
      setError("Configure primeiro o e-mail do sistema para reutilizá-lo no e-mail marketing.");
      return;
    }
    if (!marketingUsesSystem && (!current.smtpHost || !current.smtpPort || !current.senderEmail || !current.authUser)) {
      setError("Preencha host, porta, remetente e usuário SMTP.");
      return;
    }
    setBusy("save");
    setError("");
    setFeedback("");
    try {
      const payload = activeTab === "marketing" && marketingUsesSystem
        ? { useSystemConfiguration: true }
        : {
            ...(activeTab === "marketing" ? { useSystemConfiguration: false } : {}),
            smtpHost: current.smtpHost || null,
            smtpPort: current.smtpPort ? Number(current.smtpPort) : null,
            useTLS: Boolean(current.useTLS),
            senderName: current.senderName || null,
            senderEmail: current.senderEmail || null,
            authUser: current.authUser || null,
          };
      if (!marketingUsesSystem && password) payload.authPassword = password;
      if (!marketingUsesSystem && clearPassword) payload.clearAuthPassword = true;

      const endpoint = activeTab === "system"
        ? "/mail/configuration"
        : "/mail/marketing/configuration";
      const response = await api.put(endpoint, payload);
      const next = normalize(response.data);
      if (activeTab === "system") {
        setSystem(next);
        setSystemPassword("");
        setClearSystemPassword(false);
      } else {
        setMarketing(next);
        setMarketingPassword("");
        setClearMarketingPassword(false);
      }
      setFeedback("Configuração salva.");
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível salvar.");
    } finally {
      setBusy("");
    }
  };

  const sendTest = async () => {
    setBusy("test");
    setError("");
    setFeedback("");
    try {
      if (activeTab === "system") {
        await api.post("/mail/test", { to: testRecipient || undefined });
      } else {
        if (!testRecipient.trim()) {
          setError("Informe um e-mail para o teste de marketing.");
          setBusy("");
          return;
        }
        await api.post("/mail/marketing/test", { to: testRecipient.trim() });
      }
      setFeedback("E-mail de teste enviado.");
    } catch (err) {
      setError(err.response?.data?.error || "Falha ao enviar o teste.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-700"><FaEnvelope /></span>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-slate-900">Configuração de e-mail</h1>
            <InfoHint text="O e-mail do sistema cuida das notificações. No e-mail marketing você pode reutilizar essa mesma configuração ou definir um SMTP exclusivo para o CRM." />
          </div>
        </div>
        <div className="flex rounded-full bg-slate-100 p-1">
          {[
            ["system", "E-mail do sistema"],
            ["marketing", "E-mail marketing"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setActiveTab(id);
                setError("");
                setFeedback("");
              }}
              className={`rounded-full px-4 py-2 text-sm font-extrabold transition ${
                activeTab === id ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
          <FaExclamationTriangle /> {error}
        </div>
      )}
      {feedback && (
        <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
          <FaCheckCircle /> {feedback}
        </div>
      )}

      {activeTab === "marketing" && (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-base font-black text-slate-900">Como o CRM deve enviar os e-mails?</h2>
            <p className="mt-1 text-sm text-slate-500">Escolha uma opção. Você pode reutilizar o e-mail já configurado no sistema sem informar senha novamente.</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <button
              type="button"
              onClick={() => setMarketing((previous) => ({ ...previous, useSystemConfiguration: true }))}
              className={`rounded-2xl border p-4 text-left transition ${marketing.useSystemConfiguration ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-blue-200"}`}
            >
              <div className="flex items-start gap-3">
                <span className={`grid h-10 w-10 place-items-center rounded-xl ${marketing.useSystemConfiguration ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><FaEnvelope /></span>
                <span>
                  <strong className="block text-sm text-slate-900">Usar o e-mail do sistema</strong>
                  <span className="mt-1 block text-xs leading-relaxed text-slate-500">Usa automaticamente o mesmo SMTP, usuário e senha já salvos no sistema.</span>
                </span>
              </div>
              <div className="mt-3 rounded-xl bg-white/80 px-3 py-2 text-xs text-slate-600">
                {systemReady ? (
                  <>
                    <strong className="block text-slate-800">{system.senderEmail}</strong>
                    <span>{system.smtpHost}:{system.smtpPort} {system.useTLS ? "· TLS/SSL" : ""}</span>
                  </>
                ) : (
                  <span className="font-bold text-amber-700">O e-mail do sistema ainda precisa ser configurado.</span>
                )}
              </div>
            </button>

            <button
              type="button"
              onClick={() => setMarketing((previous) => ({ ...previous, useSystemConfiguration: false }))}
              className={`rounded-2xl border p-4 text-left transition ${!marketing.useSystemConfiguration ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-blue-200"}`}
            >
              <div className="flex items-start gap-3">
                <span className={`grid h-10 w-10 place-items-center rounded-xl ${!marketing.useSystemConfiguration ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><FaPaperPlane /></span>
                <span>
                  <strong className="block text-sm text-slate-900">Usar um e-mail exclusivo</strong>
                  <span className="mt-1 block text-xs leading-relaxed text-slate-500">Mantém uma configuração SMTP separada somente para campanhas do CRM.</span>
                </span>
              </div>
            </button>
          </div>
        </section>
      )}

      {marketingUsesSystem ? (
        <div className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm text-blue-800">
          <FaCheckCircle className="shrink-0 text-lg" />
          <div>
            <strong className="block">Configuração compartilhada ativada</strong>
            <span className="text-xs text-blue-700">O E-mail Marketing usará o mesmo e-mail do sistema. Nenhuma senha será copiada ou exibida.</span>
          </div>
        </div>
      ) : (
        <ConfigFields
          value={current}
          onChange={updateCurrent}
          password={password}
          setPassword={activeTab === "system" ? setSystemPassword : setMarketingPassword}
          clearPassword={clearPassword}
          setClearPassword={activeTab === "system" ? setClearSystemPassword : setClearMarketingPassword}
        />
      )}

      <div className="sticky bottom-3 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="flex min-w-[260px] flex-1 items-center gap-2">
          <input
            type="email"
            value={testRecipient}
            onChange={(event) => setTestRecipient(event.target.value)}
            placeholder="E-mail para teste"
            className="w-full max-w-sm rounded-full border border-slate-200 px-4 py-2 text-sm outline-none focus:border-blue-400"
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={busy === "test" || busy === "load"}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-blue-200 px-4 py-2 text-sm font-extrabold text-blue-700 disabled:opacity-50"
          >
            <FaPaperPlane /> Testar
          </button>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy === "save" || busy === "load"}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-extrabold text-white shadow-md disabled:opacity-50"
        >
          <FaSave /> {busy === "save" ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </div>
  );
}
